<template>
  <div class="embedded-payment">
    <section :id="identifier" />
    <div v-if="loading">
      <v-row v-if="mobile">
        <v-col cols="12">
          <v-skeleton-loader type="article@3" />
        </v-col>
      </v-row>

      <v-row v-else class="mb-2">
        <v-col cols="12">
          <v-skeleton-loader type="heading" />
        </v-col>

        <v-col>
          <v-skeleton-loader type="table-tbody" />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { TrackableEvent } from "@/analytics/TrackableEvents";
import api from "@/api";
import { useAnalytics } from "@/plugins/analytics";
import EmbeddedCheckoutService from "@/services/EmbeddedCheckoutService";
import { useCartStore } from "@/stores/cart";
import { useCheckoutStore } from "@/stores/checkout";
import { useEventsStore } from "@/stores/events";
import { useOrdersStore } from "@/stores/orders";
import { useSnackbar } from "@/stores/snackbar";
import DataResponse from "@/types/DataResponse";
import EmbeddedCheckout from "@/types/EmbeddedCheckout";
import { onUnmounted, ref, Ref } from "vue";
import { useRouter } from "vue-router";
import { useDisplay } from "vuetify";

const identifier = "embedded-checkout";

const loading: Ref<boolean> = ref(false);

const { mobile } = useDisplay();
const checkout = useCheckoutStore();
const cart = useCartStore();
const router = useRouter();
const { selected: event } = useEventsStore();

let service: EmbeddedCheckoutService | null = null;

onUnmounted(() => {
  if (service !== null) {
    service.destroy();
  }
});

async function validate(): Promise<boolean> {
  return Promise.resolve(true);
}

async function submit(): Promise<boolean> {
  return Promise.resolve(true);
}

async function cancel(): Promise<boolean> {
  return Promise.resolve(true);
}

async function enter(): Promise<void> {
  // The gateway's checkout times the payment itself
  checkout.timer.stop();

  const container = document.getElementById(identifier);

  if (container !== null) {
    container.innerHTML = "";
  }

  loading.value = true;

  const order = checkout.order;

  if (order === null) {
    throw new Error("Compra não encontrada");
  }

  const response = await api.post<DataResponse<EmbeddedCheckout>>("/api/checkout/embed", {
    order,
  });

  service = new EmbeddedCheckoutService(response.data.data.provider);
  service.mount(identifier, response.data.data, {
    onSuccess: () => {
      redirect();
    },
    onTimeout: () => {
      useSnackbar().show("Tempo de pagamento esgotado. Por favor, tente novamente.", "error");
      cart.reset();
      checkout.reset();
      router.push({
        path: "/ticket/payments",
        query: { status: "Recusado" },
      });
    },
    onCanceled: () => {
      cart.reset();
      checkout.reset();
    },
  });

  loading.value = false;
}

function redirect(): void {
  const order = checkout.order;

  if (order === null) {
    throw new Error("Compra não encontrada");
  }

  useSnackbar().show("Pagamento realizado com sucesso!", "success");

  useOrdersStore().select(order);

  cart.reset();
  checkout.reset();

  useAnalytics().collect(
    TrackableEvent.Purchase,
    {
      producer_id: event!.cliente_id,
      params: {
        currency: "BRL",
        value: order.valor_total,
        transaction_id: order.id,
        items: order.items.map(item => ({
          item_id: item.id,
          item_name: item.tipo,
          price: item.valor,
          quantity: item.quantidade,
          item_category: item.nome_cortesia ? "Cortesia" : "Normal",
          item_variant: order.evento_id,
        })),
      },
    },
  );

  router.push(`/ticket/list/${order.id}`);
}

defineExpose({
  validate,
  submit,
  cancel,
  enter,
});

</script>
